/**
 * KB-META
 * Purpose: Auth service
 * Role: ui.service
 * EntryPoints: constructor(), if(), catch(), getUserPool()
 * Inputs: public router:Router; public sessionService:SessionService; private http:HttpClient; isValid:unknown; error:unknown
 * Outputs: 
 * Validation: 
 * Dependencies: Router, SessionService, HttpClient
 * Contracts: HTTP ${ENV.authUrl}/partner/logout, HTTP ${ENV.altV2Url}/user/${userId}, HTTP ${ENV.authUrl}/partner/login, HTTP ${ENV.altV1Url}/community/user?email=${email}
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=services/auth.service; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SessionService } from './session.service';
import { CryptoService } from './crypto.service';
import { Cognito } from '../models/cognito.model';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { TokenService } from '../core/security/token.service';
import { ENV } from '../config/environment';
import { catchError, firstValueFrom, Observable, throwError, map } from 'rxjs';
import { RegistrationResponse, RegistrationRequest } from '../models/auth.model';
import { PutBucketAccelerateConfigurationCommand } from '@aws-sdk/client-s3';
import { CognitoUserPool, CognitoUser, CognitoUserAttribute } from 'amazon-cognito-identity-js';

const helper = new JwtHelperService();

interface UserPasswordDetails {
  lastPasswordChangedDate?: string;
  id: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  
  userData: any;
  genusCognitoId: any;
  public static POOL_DATA: any = {
    UserPoolId: ENV.UserPoolId,
    ClientId: ENV.ClientId
  };
  // private userPool: CognitoUserPool;
  // private genusConnectUserPool: CognitoUserPool;

  constructor(public router: Router,
    public sessionService: SessionService,
    private http: HttpClient,
    private readonly tokenService: TokenService,
    private readonly cryptoService: CryptoService
  ) {


  }

  /**
   * Sets a cookie.
   * @param name - The name of the cookie.
   * @param value - The value to store.
   * @param days - Number of days until expiration.
   */
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const secureAttr = (typeof location !== 'undefined' && location.protocol === 'https:') ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Strict${secureAttr}`;
  }

  private deleteCookie(name: string): void {
    const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const secureAttr = (typeof location !== 'undefined' && location.protocol === 'https:') ? '; Secure' : '';
    document.cookie = `${name}=; expires=${expires}; path=/; SameSite=Strict${secureAttr}`;
  }

  private deleteAuthCookies(): void {
    try { this.deleteCookie('token'); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
    try { this.deleteCookie('accessToken'); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
    try { this.deleteCookie('refreshToken'); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
    try { this.deleteCookie('encKey'); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
  }


  /**
   * Constructs HTTP headers for API requests.
   * @returns An instance of HttpHeaders with appropriate headers set.
   */
  public getHeaders(): HttpHeaders {
    const token = this.sessionService.getItem('token');
    let headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  } 

  /**
   * Calculate cookie lifetime in days from a JWT exp claim (seconds since epoch)
   */
  private getCookieDaysFromJwt(token: string): number {
    try {
      const decoded: any = helper.decodeToken(token);
      if (!decoded?.exp) return 1;
      const expMs = decoded.exp * 1000;
      const delta = Math.max(expMs - Date.now(), 5 * 60 * 1000); // at least 5 minutes
      return delta / 864e5;
    } catch {
      return 1;
    }
  }


  async createpartner(user: any): Promise<RegistrationResponse> {
    try {
      
      const registrationData: RegistrationRequest = {
        userName: user.userName.toLowerCase(),
        email: user.email.toLowerCase(),
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        role: user.role,
        partnerCode: user.partnerCode,
        password: user.password,
        partnerName: user.partnerName,
        partnerAddress: user.PartnerAddress || '',
        partnerState: user.partnerState || '',
        partnerCity: user.partnerCity || '',
        partnerCountry: user.Partnercountry || '',
        contact1: user.contact1 || '',
        website: user.website || '',
        contact2: '',
        partnerLogo: '',
        partnerlogourl: '',
        genusAssignedPhone: ''
      };
      const response = await firstValueFrom(
        this.http.post<RegistrationResponse>(`${ENV.authUrl}/partner/createpartner`, registrationData, { headers: this.getHeaders() })
          .pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.error?.error?.includes('password')) {
                throw new Error('Password does not meet requirements');
              }
              throw error.error || error;
            })
          )
      );
      
      return response;
    } catch (error) {
      throw error instanceof Error ? error : new Error(JSON.stringify(error));
    }
  }

  async SignOut(): Promise<void> {
    try {
      const accessToken = this.sessionService.getItem('accessToken');
      // if (accessToken) {
      //   await firstValueFrom(
      //     this.http.post(`${ENV.authUrl}/partner/logout`, { accessToken }).pipe(
      //       catchError((error: HttpErrorResponse) => {
      //         throw error.error?.message || error.message || 'Failed to logout';
      //       })
      //     )
      //   );
      // }

      this.sessionService.removeItem('user');
      this.sessionService.clear();
      this.deleteAuthCookies();
      try { this.tokenService.clear(); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
      this.router.navigate(['login']);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear session and redirect even if API call fails
      this.sessionService.removeItem('user');
      this.sessionService.clear();
      this.deleteAuthCookies();
      try { this.tokenService.clear(); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
      this.router.navigate(['login']);
    }
  }

  /**
   * Checks if the user's password has expired (older than 90 days)
   * @param lastPasswordChangedDate The date when password was last changed
   * @returns boolean indicating if password has expired
   */
  private isPasswordExpired(lastPasswordChangedDate: string | undefined): boolean {
    if (!lastPasswordChangedDate) return true;
    
    const lastChanged = new Date(lastPasswordChangedDate);
    const now = new Date();
    const daysDifference = Math.floor((now.getTime() - lastChanged.getTime()) / (1000 * 60 * 60 * 24));
    
    return daysDifference >= 90;
  }

  /**
   * Fetches the user's password details from the database
   * @param userId The user's ID
   * @returns Promise with user password details
   */
  private async getUserPasswordDetails(userId: string): Promise<UserPasswordDetails> {
    try {
      const response = await firstValueFrom(this.http.get<UserPasswordDetails>(`${ENV.altV2Url}/user/${userId}`));
      if (!response) {
        throw new Error('Failed to fetch user password details');
      }
      return response;
    } catch (error) {
      console.error('Error fetching user password details:', error);
      throw error;
    }
  }

  /**
   * Updates the last password changed date in the database
   * @param userId The user's ID
   * @returns Promise indicating success
   */
  private async updateLastPasswordChangedDate(userId: string): Promise<void> {
    try {
      const updateData = {
        lastPasswordChangedDate: new Date().toISOString()
      };
      
      await firstValueFrom(this.http.post(`${ENV.altV2Url}/user/${userId}`, updateData));
    } catch (error) {
      console.error('Error updating password change date:', error);
      throw error;
    }
  }

  async login(username: string, password: string): Promise<any> {
    try {
      const loginResponse = await firstValueFrom(this.http.post(`${ENV.authUrl}/partner/login`, { username, password })) as {
        accessToken: string,
        encryptionKey: string,
        idToken: string,
        refreshToken: string,
        challengeName?: string,
        session?: string
      };

      if (loginResponse.challengeName) {
        return {
          challengeName: loginResponse.challengeName,
          session: loginResponse.session,
          username
        };
      }

      if (!loginResponse || !loginResponse.idToken) {
        throw new Error('Invalid login response');
      }

      this.sessionService.setEncryptionKey(loginResponse.encryptionKey);

      // Store the tokens in cookies (expires in 1 day)
      this.setCookie('token', loginResponse.idToken, 1);
      this.setCookie('accessToken', loginResponse.accessToken, 1);
      this.setCookie('refreshToken', loginResponse.refreshToken, 1);

      // Do not set 'user' to a plain string; wait until decoded token and details are available.
      this.sessionService.setItem('token', loginResponse.idToken);
      this.tokenService.set(loginResponse.idToken);
      this.sessionService.setItem('accessToken', loginResponse.accessToken);
      this.sessionService.setItem('refreshToken', loginResponse.refreshToken);
      // Persist encryption key for this browser session and set wrapped cookie
      try {
        this.cryptoService.persistKeyForSession(loginResponse.refreshToken);
        const wrapped = this.cryptoService.wrapKeyForCookie(loginResponse.refreshToken);
        if (wrapped) {
          const days = this.getCookieDaysFromJwt(loginResponse.idToken);
          this.setCookie('encKey', wrapped, days);
        }
      } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }

      // Decode the idToken to get user attributes
      const decodedToken = helper.decodeToken(loginResponse.idToken);
      // encKey cookie set above as wrapped blob
      
      if (decodedToken) {
        const userParam = await firstValueFrom(this.getUserDetails(decodedToken.email));
        const userDetails = userParam.items[0];

        // Check password expiration
        let passwordDetails: UserPasswordDetails | null = null;
        if (userDetails && userDetails.id) {
          passwordDetails = await this.getUserPasswordDetails(userDetails.id);
          const isExpired = this.isPasswordExpired(passwordDetails.lastPasswordChangedDate);
          if (isExpired) {
            return {
              challengeName: 'NEW_PASSWORD_REQUIRED',
              session: loginResponse.session,
              username,
              message: 'Password has expired. Please change your password.'
            };
          }
        }

        const localUserDetails = {
          email: decodedToken.email,
          given_name: decodedToken['given_name'],
          family_name: decodedToken['family_name'],
          phone_number: decodedToken['phone_number'],
          profile: decodedToken.profile,
          'custom:partner_code': decodedToken['custom:partner_code'],
          preferred_username: decodedToken['preferred_username'],
          custom_partner_code: decodedToken['custom:partner_code'],
          genusCognitoId: userDetails && userDetails['cognitoId'] ? userDetails['cognitoId'] : '',
          lastPasswordChangedDate: passwordDetails?.lastPasswordChangedDate,
          ...decodedToken,
          ...userDetails
        };
        
        this.sessionService.setItem('user', localUserDetails);
        // Ensure session writes have flushed before navigation/guard checks
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const { idToken, ...otherLoginResponse } = loginResponse;
        return {
          idToken: {
            payload: decodedToken
          },
          ...otherLoginResponse
        };
      } else {
        throw new Error('Failed to decode token');
      }
    } catch (error: any) {
      if (error?.message?.includes('newPasswordRequired')) {
        throw 'newPasswordRequired';
      }
      throw error.message || JSON.stringify(error);
    }
  }

  getUserDetails(email: string): Observable<any> {
    return this.http.get(`${ENV.altV1Url}/community/user?email=${email}`);
  }
  getAccessToken(): string | null {
    // return this.getCookie('accessToken'); // Retrieve token from cookie
    return this.sessionService.getItem('token');
  }

  isTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    const decoded: any = helper.decodeToken(token);
   
    const now = Math.floor(Date.now() / 1000);
   
    return decoded.exp < now; // Return true if expired
  }

  getCookie(name: string): string | null {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find(row => row.startsWith(name + '='));
    return cookie ? cookie.split('=')[1] : null;
  }

  get isLoggedIn(): boolean {
    try {
      const userDetailsString = this.sessionService.getItem('user');

      if (!userDetailsString) {
        return false;
      }
      const token = this.sessionService.getItem('token');
      if (!token) {
        return false;
      }

      const decodedToken = helper.decodeToken(token);
      const expirationDate = helper.getTokenExpirationDate(token);
      const isExpired = helper.isTokenExpired(token);

      // Additional checks can be added here based on decodedToken contents
      return !isExpired;
    } catch (e) {
      console.error('Error checking login status:', e);
      return false;
    }
  }

  async register(user: Cognito): Promise<any> {
    // Prepare payload according to the provided schema
    const payload = {
      userName: user.userName,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      role: user.role,
      partnerCode: user.partnerCode,
      password: user.password
    };
    try {
      const response = await firstValueFrom(this.http.post(`${ENV.authUrl}/partner/register`, payload));
      return response;
    } catch (error) {
      console.error('Error registering partner:', error);
      throw error;
    }
  }



  /**
   * Initiates the password recovery process using the API endpoint.
   * @param username The username of the user.
   * @returns Promise that resolves with the API response
   */
  public recoverPassword(username: string): Promise<any> {
    return firstValueFrom(
      this.http.post(`${ENV.authUrl}/partner/forgot-password`, { username })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw error.error || error;
          })
        )
    );
  }

  /**
   * Changes the password for the currently authenticated user using the API endpoint.
   * @param oldPassword The current password of the user.
   * @param newPassword The new password to set.
   * @returns A promise that resolves when the password is changed successfully.
   */
  public async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
    const accessToken = this.sessionService.getItem('accessToken');
    if (!accessToken) {
      return Promise.reject(new Error('No access token found'));
    }

    try {
      // Change password in authentication system
      await firstValueFrom(
        this.http.post(`${ENV.authUrl}/partner/change-password`, {
          accessToken,
          oldPassword,
          newPassword
        }).pipe(
          catchError((error: HttpErrorResponse) => {
            throw error;
          })
        )
      );

      // Update last password change date in database
      const userDetails = this.sessionService.getItem('user');
      if (userDetails && userDetails.id) {
        await this.updateLastPasswordChangedDate(userDetails.id);
      } else {
        console.warn('User ID not found in session, could not update password change date');
      }

      return true;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  /**
   * Resets the password for another user (admin-initiated).
   * @param username The username of the user whose password should be reset.
   * @param newPassword The new password to set.
   * @returns A promise that resolves with the API response containing reset details.
   */
  public async adminResetPassword(username: string, newPassword: string): Promise<any> {
    let accessToken = this.sessionService.getItem('accessToken');
    const mask = (token: string | null | undefined) => {
      if (!token || token.length < 10) {
        return token || 'null';
      }
      return `${token.substring(0, 5)}...${token.substring(token.length - 5)}`;
    };

    if (!accessToken) {
      console.warn('[AuthService] adminResetPassword: accessToken missing from session.');
      return Promise.reject(new Error('No access token found'));
    }

    try {
      // Validate token freshness with small leeway; refresh if necessary
      let tokenExpired = helper.isTokenExpired(accessToken);
      try {
        const expDate = helper.getTokenExpirationDate(accessToken);
        if (expDate) {
          const secondsToExpiry = Math.floor((expDate.getTime() - Date.now()) / 1000);
          // Consider expiring soon (< 60s) as expired to avoid race conditions
          if (secondsToExpiry <= 60) {
            tokenExpired = true;
            console.info(`[AuthService] adminResetPassword: accessToken expiring in ${secondsToExpiry}s; refreshing.`);
          }
        }
      } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
      if (tokenExpired) {
        console.info('[AuthService] adminResetPassword: accessToken expired, attempting refresh.');
        try {
          await firstValueFrom(this.refreshToken());
          accessToken = this.sessionService.getItem('accessToken');
        } catch (refreshError) {
          console.error('[AuthService] adminResetPassword: token refresh failed.', refreshError);
          throw new Error('Access token expired and could not be refreshed. Please log in again.');
        }

        if (!accessToken) {
          console.error('[AuthService] adminResetPassword: refreshed accessToken not available.');
          throw new Error('Failed to retrieve refreshed access token.');
        }

        if (helper.isTokenExpired(accessToken)) {
          console.error('[AuthService] adminResetPassword: refreshed token still appears expired.');
          throw new Error('Refreshed access token is invalid.');
        }
      }

      console.debug('[AuthService] adminResetPassword: using accessToken', mask(accessToken));

      // Reset password in authentication system (admin action)
      const response = await firstValueFrom(
        this.http.post(`${ENV.authUrl}/partner/admin/reset-password`, {
          accessToken,
          username,
          newPassword
        }).pipe(
          catchError((error: HttpErrorResponse) => {
            throw error;
          })
        )
      );

      return response;
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }

  /**
   * Changes the password for the currently authenticated user using the API endpoint.
   * @param username The current username.
   * @param newPassword The new password to set.
   * @returns A promise that resolves when the password is changed successfully.
   */
  public async newPasswordRequired(username: string, newPassword: string, session: string): Promise<boolean> {
    const accessToken = this.sessionService.getItem('accessToken');
    if (!accessToken) {
      return Promise.reject(new Error('No access token found'));
    }

    await firstValueFrom(
      this.http.post(`${ENV.authUrl}/partner/set-new-password`, {
        username,
        newPassword,
        session
       
      }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw error;
        })
      )
    );
    return true;
  }

  /**
   * Confirms the new password using the verification code through the API.
   * @param userName The username of the user.
   * @param verificationCode The verification code sent to the user.
   * @param newPassword The new password to set.
   * @returns A promise that resolves to true if confirmation was successful.
   */
  public async confirmPassword(userName: string, verificationCode: string, newPassword: string): Promise<boolean> {
    await firstValueFrom(
      this.http.post(`${ENV.authUrl}/partner/confirm-password`, {
        username: userName,
        confirmationCode: verificationCode,
        newPassword: newPassword
      }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw error;
        })
      )
    );
    return true;
  }
  getUserPool() {
    return new CognitoUserPool(AuthService.POOL_DATA);
  }
  /**
   * Gets the current authenticated user from Cognito.
   * @returns The current CognitoUser or null if not authenticated.
   */
  getCurrentUser() {
    let cognitoUser: CognitoUser | null = this.getUserPool().getCurrentUser();
    if (!ENV.production) {
      console.log(cognitoUser);
    }
    if (cognitoUser != null) {
      cognitoUser.getSession(function (err :any, session :any) {
        if (err) {
          return;
        }
      });
    }
    return cognitoUser;
  }
  /**
   * Updates the user's profile attributes using the API endpoint.
   * @param item - An object containing user profile details.
   * @returns A Promise that resolves when the profile is successfully updated.
   */
  updateProfile(item: { phone: string; firstName: string; lastName: string; badgeName?: string }) {
    const accessToken = this.sessionService.getItem('accessToken');
    if (!accessToken) {
      return Promise.reject(new Error('No access token found'));
    }

    const payload: any = {
      accessToken,
      phone_number: item.phone,
      given_name: item.firstName,
      family_name: item.lastName
    };

    // Add badge name if provided
    if (item.badgeName) {
      payload.badgeName = item.badgeName;
    }

    return firstValueFrom(
      this.http.post(`${ENV.authUrl}/partner/update-attributes`, payload).pipe(
        catchError((error: HttpErrorResponse) => {
          throw error;
        })
      )
    );
  }


  async registerForGenusConnect(user: any): Promise<any> {
    // Use the API endpoint to register a user for GenusConnect
    const payload = {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      role: user.role
    };
    try {
      const response = await firstValueFrom(this.http.post(`${ENV.authUrl}/genusconnect/register`, payload));
      return response;
    } catch (error) {
      console.error('Error registering for GenusConnect:', error);
      throw error;
    }
  }

  async verifyMfaCode(mfaCode: string,username:string, session: string,challengeName: string): Promise<any>{
  
    try {
      const loginResponse = await firstValueFrom(this.http.post(`${ENV.authUrl}/partner/verify-mfa`,  { username, session, mfaCode, challengeName})) as {
        accessToken: string,
        encryptionKey: string,
        idToken: string,
        refreshToken: string
      };

      if (!loginResponse || !loginResponse.idToken) {
        throw new Error('Invalid login response');
      }
      this.sessionService.setEncryptionKey(loginResponse.encryptionKey);

      // Store the tokens in cookies (expires in 1 day)
      this.setCookie('token', loginResponse.idToken, 1);
      this.setCookie('accessToken', loginResponse.accessToken, 1);
      this.setCookie('refreshToken', loginResponse.refreshToken, 1);

      // Store tokens first
      this.sessionService.setItem('token', loginResponse.idToken);
      this.tokenService.set(loginResponse.idToken);
      this.sessionService.setItem('accessToken', loginResponse.accessToken);
      this.sessionService.setItem('refreshToken', loginResponse.refreshToken);
      // Persist encryption key for this browser session and set wrapped cookie
      try {
        this.cryptoService.persistKeyForSession(loginResponse.refreshToken);
        const wrapped = this.cryptoService.wrapKeyForCookie(loginResponse.refreshToken);
        if (wrapped) {
          const days = this.getCookieDaysFromJwt(loginResponse.idToken);
          this.setCookie('encKey', wrapped, days);
        }
      } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }

      // Decode the idToken to get user attributes
      const decodedToken = helper.decodeToken(loginResponse.idToken);
      // encKey cookie set above as wrapped blob
      if (!ENV.production) {
        console.log(decodedToken);
      }
      if (decodedToken) {
        // Fetch complete user details from database (including genusCognitoId)
        const userParam = await firstValueFrom(this.getUserDetails(decodedToken.email));
        const userDetails = userParam.items[0];

        // Build complete user object with all fields - set only once
        const localUserDetails = {
          email: decodedToken.email,
          given_name: decodedToken['given_name'],
          family_name: decodedToken['family_name'],
          phone_number: decodedToken['phone_number'],
          profile: decodedToken.profile,
          'custom:partner_code': decodedToken['custom:partner_code'],
          preferred_username: decodedToken['preferred_username'],
          custom_partner_code: decodedToken['custom:partner_code'],
          genusCognitoId: userDetails?.cognitoId || '',
          // Add any other attributes that are present in the token
          ...decodedToken,
          ...userDetails
        };
        
        // Store complete user object once
        this.sessionService.setItem('user', localUserDetails);

        const { idToken, ...otherLoginResponse } = loginResponse;
        return {
          idToken: {
            payload: decodedToken
          },
          ...otherLoginResponse
        };
      } else {
        throw new Error('Failed to decode token');
      }
    } catch (error: any) {
      // If the error indicates password change is required
      if (error?.message?.includes('newPasswordRequired')) {
        throw 'newPasswordRequired';
      }
      throw error.message || JSON.stringify(error);
    }
  }


  // async confirmRegister(email: string, code: string, isGenusConnect: boolean = false): Promise<any> {
  //   const userPool = isGenusConnect ? this.genusConnectUserPool : this.userPool;
  //   const userData = {
  //     Username: email,
  //     Pool: userPool
  //   };
  //   const cognitoUser = new CognitoUser(userData);
    
  //   return new Promise((resolve, reject) => {
  //     cognitoUser.confirmRegistration(code, true, (err, result) => {
  //       if (err) {
  //         reject(err);
  //       } else {
  //         resolve(result);
  //       }
  //     });
  //   });
  // }
  /**
   * Refreshes the authentication token using the refresh token.
   * @returns An Observable that emits the new token or throws an error.
   */
  refreshToken(): Observable<string> {
    const refreshToken = this.sessionService.getItem('refreshToken');
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http.post<{ idToken: string, accessToken: string }>(`${ENV.authUrl}/partner/refresh-token`, { refreshToken })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error refreshing token:', error);
          return throwError(() => new Error('Failed to refresh token'));
        }),
        map(response => {
          if (!response || !response.idToken) {
            throw new Error('Invalid refresh token response');
          }
          this.setCookie('token', response.idToken, 1);
          this.setCookie('accessToken', response.accessToken, 1);
           this.sessionService.setItem('token', response.idToken);
          this.tokenService.set(response.idToken);
          this.sessionService.setItem('accessToken', response.accessToken);
          return response.idToken;
        })
      );

  }

  /**
   * Generates a suggested username based on company name and email
   */
  generateUsername(companyName: string, email: string): { suggestedUsername: string; partnerCode: string; isAvailable: boolean } {
    // Extract username from email (part before @)
    const emailUsername = email.split('@')[0];
    
    // Create company abbreviation from initials
    const companyInitials = companyName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('');
    
    // Generate suggested username: CompanyInitials-emailUsername
    const suggestedUsername = `${companyInitials}-${emailUsername}`.toLowerCase();
    
    // Generate partner code (you may want to make this more sophisticated)
    const partnerCode = `${companyInitials}${Date.now().toString().slice(-4)}`;
    
    return {
      suggestedUsername,
      partnerCode,
      isAvailable: true // You'll want to check this against existing usernames
    };
  }

  /**
   * Sends phone verification code
   */
  async sendPhoneVerification(phoneNumber: string, method: 'sms' | 'voice' = 'sms'): Promise<{ success: boolean; message: string; verificationId?: string }> {
    try {
      const response = await firstValueFrom(
        this.http.post<{ verificationId?: string; message?: string }>(
          `${ENV.authUrl}/partner/send-phone-verification`,
          { phoneNumber, method }
        ).pipe(
          catchError((error: HttpErrorResponse) => {
            throw error;
          })
        )
      );
      return {
        success: true,
        message: response?.message || 'Verification code sent successfully',
        verificationId: response?.verificationId
      };
    } catch (error) {
      return {
        success: false,
        message: 'Failed to send verification code'
      };
    }
  }

  /**
   * Verifies phone number with code
   */
  async verifyPhoneCode(phoneNumber: string, code: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<{ verified?: boolean }>(
          `${ENV.authUrl}/partner/verify-phone-code`,
          { phoneNumber, code }
        ).pipe(
          catchError((error: HttpErrorResponse) => {
            throw error;
          })
        )
      );
      return response?.verified === true;
    } catch {
      return false;
    }
  }

  /**
   * Verifies private PIN (hardcoded for now)
   */
  async verifyPrivatePin(pin: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<{ verified?: boolean }>(
          `${ENV.authUrl}/partner/verify-private-pin`,
          { pin }
        ).pipe(
          catchError((error: HttpErrorResponse) => {
            throw error;
          })
        )
      );
      return response?.verified === true;
    } catch {
      return false;
    }
  }

  /**
   * Creates public partner registration
   */
  async createPublicPartner(registrationData: any): Promise<RegistrationResponse> {
    try {
      const partnerData = {
        userName: registrationData.suggestedUsername!.toLowerCase(),
        email: registrationData.email.toLowerCase(),
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        phone: registrationData.phone,
        role: 'partner',
        partnerCode: registrationData.partnerCode,
        password: registrationData.password,
        partnerName: registrationData.companyName,
        partnerAddress: registrationData.address,
        partnerState: registrationData.state,
        partnerCity: registrationData.city,
        partnerCountry: registrationData.country,
        contact1: registrationData.phone,
        website: '',
        // Add industry type to tags or custom field
        tags1: registrationData.industryType
      };
      
      return await this.createpartner(partnerData);
    } catch (error) {
      throw error;
    }
  }

}
